/**
 * detached-failure.js — довести до вызывающего отказ, который прилетел МИМО
 * его цепочки промисов.
 *
 * ЗАЧЕМ. Сторонний движок, который работает через воркер, обычно кормит его
 * из СВОЕГО `worker.onmessage`. Исключение, брошенное там, не связано с тем
 * промисом, который мы ждём: наш `await` не завершается НИКОГДА, `.catch()`
 * не зовётся, а на экране остаётся полоса прогресса, которая больше не
 * сдвинется. Ошибка при этом улетает на страницу необработанной — и в
 * Sentry выглядит как «ошибка без единого нашего кадра».
 *
 * Один и тот же класс пришёл тремя задачами:
 *   - TIMBRICA-FRONTEND-H8  gif.js, `Data cannot be cloned, out of memory`
 *                           (замерено: промис висел 8 с и дольше);
 *   - TIMBRICA-FRONTEND-DR  tesseract, `RuntimeError: null function`,
 *                           бросок внутри `t.onmessage`;
 *   - TIMBRICA-FRONTEND-G3  TF.js/face-api, отцепленный отказ шейдера
 *                           (лечился тем же приёмом вручную).
 *
 * ГРАНИЦЫ. Слушаем ровно столько, сколько идёт НАША работа, и берём только
 * отказы своего класса — предикат `match` обязателен, потому что чужие
 * ошибки страницы нас не касаются и должны остаться необработанными.
 * Совпавший отказ помечается `detachedAdopted`, чтобы вызывающий мог
 * отличить «движок сломался» от собственного отказа задачи.
 *
 * ⛔ НЕ сторож молчания. Здесь нет тайм-аутов: убивать работу за то, что она
 * долгая, этот проект уже пробовал и заплатил здоровыми воркерами
 * (mp3-энкодер, 12 убийств за 5 часов). Реагируем только на НАСТОЯЩИЙ отказ.
 */

/**
 * @param {function(): Promise} runner   работа, чей отказ может уйти мимо
 * @param {{match: function(Error=, Event=): boolean, tag?: string, target?: EventTarget}} opts
 * @returns {Promise} тот же результат, но отказ доходит
 */
export function guard(runner, opts) {
    var o = opts || {};
    var match = o.match;
    // `target` — шов для проверки: по умолчанию окно страницы.
    var target = o.target || (typeof window !== 'undefined' ? window : null);
    if (typeof match !== 'function') {
        // Без предиката примитив превратился бы в ловушку чужих ошибок.
        return Promise.reject(new Error('detached-guard: match required'));
    }
    if (!target) return Promise.resolve().then(runner);

    return new Promise(function (resolve, reject) {
        var done = false;

        function settle(fn, v) {
            if (done) return;
            done = true;
            target.removeEventListener('error', onEvent);
            target.removeEventListener('unhandledrejection', onEvent);
            fn(v);
        }
        function onEvent(ev) {
            if (done) return;
            var err = ev && (ev.error || ev.reason);
            var msg = String((err && err.message) || (ev && ev.message) || '');
            var hit = false;
            try { hit = !!match(err, ev); } catch (_) { hit = false; }
            if (!hit) return;
            if (ev.preventDefault) ev.preventDefault();   // отказ принят нами
            var out = (err instanceof Error) ? err : new Error(msg || 'detached-failure');
            out.detachedAdopted = true;
            if (o.tag) out.detachedTag = o.tag;
            settle(reject, out);
        }

        target.addEventListener('error', onEvent);
        target.addEventListener('unhandledrejection', onEvent);

        var p;
        try { p = runner(); } catch (e) { settle(reject, e); return; }
        Promise.resolve(p).then(
            function (v) { settle(resolve, v); },
            function (e) { settle(reject, e); }
        );
    });
}

/* Потребители грузятся по-разному: /photo-to-gif и /draw-animation берут
   модулем через import, а классические скрипты тулов (/pdf-editor) читают
   глобаль. Поэтому модуль ещё и публикует себя. */
if (typeof window !== 'undefined' && !window.__detached) {
    window.__detached = { guard: guard };
}
